import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Papa from 'papaparse';
import { vendorRspReport } from '../../actions/invoiceDateActions';
import { FileDown, Search, Clock, AlertCircle, FileText, Percent, Loader2, List } from 'lucide-react';

const VendorResponseReport = () => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [page, setPage] = useState(0);
    const [showAll, setShowAll] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [openstart, setOpenStart] = useState(false);
    const [openEnd, setOpenEnd] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const dispatch = useDispatch();
    const vendorReportState = useSelector(state => state.vendorspReport || {});
    const { data: reduxData, summary: reduxSummary, loading: reduxLoading, error: reduxError } = vendorReportState;
    
    var summaryData = reduxSummary;

    const handleInvoiceClick = (invoiceId) => {
        window.open(`/invoice/${invoiceId}`, '_blank');
    };

    // Reset report data when component initializes/unmounts or when dates change
    useEffect(() => {
        setReportData([]);
        setError(null);
        return () => {
            setReportData([]);
            setError(null);
        };
    }, [startDate, endDate]);

    // Reset report data when component initializes/unmounts or when dates change
    useEffect(() => {
        dispatch({ type: 'VENDOR_REPORT_RESET' });
        return () => {
            dispatch({ type: 'VENDOR_REPORT_RESET' });
        };
    }, [dispatch, startDate, endDate]);

    // Reset report data when component mounts/unmounts
    useEffect(() => {
        // Reset on mount
        dispatch({ type: 'VENDOR_REPORT_RESET' });
        
        // Reset on unmount
        return () => {
            dispatch({ type: 'VENDOR_REPORT_RESET' });
        };
    }, []); // Empty dependency array so it only runs on mount/unmount

    useEffect(() => {
        if (reduxData && Array.isArray(reduxData)) {
            setReportData(reduxData);
        }
    }, [reduxData]);

    useEffect(() => {
        setIsLoading(reduxLoading);
    }, [reduxLoading]);

    useEffect(() => {
        if (reduxError) {
            setError(reduxError);
        }
    }, [reduxError]);

    const handleGenerateReport = () => {
        if (!startDate || !endDate) {
            setError("Please select both start and end dates");
            return;
        }

        setIsLoading(true);
        setError(null);

        var formattedStartDate = formatDate(startDate);
        var formattedEndDate = formatDate(endDate);

        dispatch(vendorRspReport(formattedStartDate, formattedEndDate))
            .then(function(response) {
                if (response && response.invoice_data) {
                    setReportData(response.invoice_data);
                }
                setIsLoading(false);
            })
            .catch(function(error) {
                setError(error.message || "Failed to fetch report data");
                setIsLoading(false);
            });
    };

    function formatDate(date) {
        if (!date) return '';
        var year = date.getFullYear();
        var month = (date.getMonth() + 1).toString().padStart(2, '0');
        var day = date.getDate().toString().padStart(2, '0');
        return year + '-' + month + '-' + day;
    }

    const handleExport = () => {
        if (!reportData.length) {
            setError("No data to export");
            return;
        }
    
        const exportData = reportData.map(item => ({
            'Invoice #': item.invoice_id,
            'Service Call Date': item.service_call_date,
            'Pickup Address': item.pickup_address,
            'Input Method': item.input_method === 'System' ? 'D2D' : 
                               item.input_method === 'Direct' ? 'ARS' : 
                               item.input_method,
            'Response Time (min)': item.response_time,
            'Late': item.is_late ? 'Yes' : 'No'
        }));
    
        const csv = Papa.unparse(exportData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        var formattedStartDate = formatDate(startDate);
        var formattedEndDate = formatDate(endDate);
        var link = document.createElement("a");
        link.href = url;
        link.download = "Response_Report_" + formattedStartDate + "_" + formattedEndDate + ".csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const toggleShowAll = () => {
        setShowAll(!showAll);
        setPage(0);
    };

    useEffect(() => {
        if (reduxData && reduxData.length > 0) {
            setReportData(reduxData);
            setPage(0);
        }
    }, [reduxData]);

    return (
        <div className="p-6 max-w-full">
            <h1 className="text-2xl font-bold text-blue-600 mb-8">
                Response Report
            </h1>

            {/* Date Picker Section */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <div className="flex flex-wrap gap-4 items-center">
                        <DatePicker
                            label="Start Date"
                            value={startDate}
                            open={openstart}
                            onClose={() => setOpenStart(false)}
                            onChange={(newValue) => setStartDate(newValue)}
                            slotProps={{ 
                                textField: { 
                                    variant: 'outlined',
                                    size: 'small',
                                    onClick: () => setOpenStart(true),
                                    className: 'w-48'
                                } 
                            }}
                        />
                        <DatePicker
                            label="End Date"
                            value={endDate}
                            open={openEnd}
                            onClose={() => setOpenEnd(false)}
                            onChange={(newValue) => setEndDate(newValue)}
                            slotProps={{ 
                                textField: { 
                                    variant: 'outlined',
                                    size: 'small',
                                    onClick: () => setOpenEnd(true),
                                    className: 'w-48'
                                } 
                            }}
                        />
                        <button
                            onClick={handleGenerateReport}
                            disabled={isLoading}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Search className="w-4 h-4 mr-2" />
                            )}
                            Go
                        </button>
                        <button
                            onClick={handleExport}
                            disabled={!reportData.length}
                            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FileDown className="w-4 h-4 mr-2" />
                            Export
                        </button>
                    </div>
                </LocalizationProvider>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-md">
                    {error}
                </div>
            )}

            {/* Summary Cards */}
            {reportData.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow-md p-6 transform hover:scale-105 transition-transform duration-200">
                        <div className="flex items-center gap-2 text-gray-600 mb-2">
                            <FileText className="w-5 h-5 text-blue-600" />
                            <span className="text-base">Total Invoices</span>
                        </div>
                        <div className="text-2xl font-bold text-blue-600">
                            {summaryData.totalInvoices}
                        </div>
                    </div>
                    
                    <div className="bg-white rounded-lg shadow-md p-6 transform hover:scale-105 transition-transform duration-200">
                        <div className="flex items-center gap-2 text-gray-600 mb-2">
                            <Clock className="w-5 h-5 text-blue-600" />
                            <span className="text-base">Avg Response Time</span>
                        </div>
                        <div className="text-2xl font-bold text-blue-600">
                            {summaryData.avgResponse.toFixed(1)} min
                        </div>
                    </div>
                    
                    <div className="bg-white rounded-lg shadow-md p-6 transform hover:scale-105 transition-transform duration-200">
                        <div className="flex items-center gap-2 text-gray-600 mb-2">
                            <AlertCircle className="w-5 h-5 text-blue-600" />
                            <span className="text-base">Late Responses</span>
                        </div>
                        <div className="text-2xl font-bold text-blue-600">
                            {summaryData.totalLates}
                        </div>
                    </div>
                    
                    <div className="bg-white rounded-lg shadow-md p-6 transform hover:scale-105 transition-transform duration-200">
                        <div className="flex items-center gap-2 text-gray-600 mb-2">
                            <Percent className="w-5 h-5 text-blue-600" />
                            <span className="text-base">Percent Late</span>
                        </div>
                        <div className="text-2xl font-bold text-blue-600">
                            {summaryData.percentLate.toFixed(1)}%
                        </div>
                    </div>
                </div>
            )}

            {/* Data Table */}
            {reportData.length > 0 && (
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-blue-600 text-white">
                                    <th className="px-6 py-3 text-left text-lg font-medium uppercase tracking-wider">
                                        Invoice #
                                    </th>
                                    <th className="px-6 py-3 text-left text-lg font-medium uppercase tracking-wider">
                                        Service Call Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-lg font-medium uppercase tracking-wider">
                                        Pickup Address
                                    </th>
                                    <th className="px-6 py-3 text-left text-lg font-medium uppercase tracking-wider">
                                        Input Method
                                    </th>
                                    <th className="px-6 py-3 text-left text-lg font-medium uppercase tracking-wider">
                                        Response Time (min)
                                    </th>
                                    <th className="px-6 py-3 text-left text-lg font-medium uppercase tracking-wider">
                                        Late
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {reportData
                                    .slice(showAll ? 0 : page * rowsPerPage, showAll ? reportData.length : page * rowsPerPage + rowsPerPage)
                                    .map((data, index) => (
                                        <tr 
                                            key={data.invoice_id || index}
                                            className="hover:bg-gray-50"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap text-lg">
                                                <span 
                                                    onClick={() => handleInvoiceClick(data.invoice_id)}
                                                    className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
                                                >
                                                    {data.invoice_id}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-lg">
                                                {data.service_call_date}
                                            </td>
                                            <td className="px-6 py-4 text-lg">
                                                {data.pickup_address}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-lg">
                                                {data.input_method === 'System' ? 'D2D' : 
                                                 data.input_method === 'Direct' ? 'ARS' : 
                                                 data.input_method}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-lg">
                                                {data.response_time}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-3 py-1 rounded-full text-base font-medium ${
                                                    data.is_late 
                                                        ? 'bg-red-100 text-red-800' 
                                                        : 'bg-green-100 text-green-800'
                                                }`}>
                                                    {data.is_late ? 'Yes' : 'No'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
                        <div className="flex-1 flex flex-col sm:flex-row justify-between items-center gap-4">
                            <div className="text-lg text-gray-700">
                                Showing {showAll ? 'all' : `${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, reportData.length)}`} of {reportData.length} results
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={toggleShowAll}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-lg font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <List className="w-4 h-4 mr-2" />
                                    {showAll ? 'Show Less' : 'Show All'}
                                </button>
                                {!showAll && (
                                    <>
                                        <button
                                            onClick={() => setPage(Math.max(0, page - 1))}
                                            disabled={page === 0}
                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-lg font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => setPage(Math.min(Math.ceil(reportData.length / rowsPerPage) - 1, page + 1))}
                                            disabled={page >= Math.ceil(reportData.length / rowsPerPage) - 1}
                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-lg font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Next
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorResponseReport;